import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    PENDING, IDLE, VIEW, EDIT,
    PUBLISHED, UNPUBLISHED,
    TYPE_A, TYPE_B,
    PROVEN_NO, PROVEN_IN_USE, PRIOR_USE,
} from "const"
import {
    SilDevice,
    SilDeviceCreate,
    SilProjectDevice,
    SilProjectDeviceCreate,
} from "server/models"
import {
    useInput,
    StateType,
    replaceHistoryState,
    forceRerenderContent,
} from "client/utils"
import {
    StoreType,
    SilDeviceValidation,

    LoadSilDeviceById,
    LoadAccountPackageUsage,
    GetMaxSilDevices,
    SaveSilDevice,
    SilProjectDeviceUpdateByFilters,
    SilDeviceUpdateRequired,
    SilProjectDeviceUpdateRequired,
    CountCalculatedSafetyFunctions,
    UpdateSilResultProjectDevice,
    IncreaseSilCalculations,

    closeModal,
    setSilDeviceEdit,
    setSilDeviceReloadList,
    setSilDeviceReloadView,
    setSilDeviceReloadEdit,
    setSystemPackageUsage,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    Textarea,
    Feedback,
    FileLoader,
    SelectDropdown,
    SelectDropdownItem,
    RadioButton,
    CheckboxWrapper,
    CheckButton,
    Collapse,
    Button,
    SpinnerIcon,

    ConfirmWrapper,
    Confirm,
    ConfirmTitle,
    ConfirmDescription,
    ConfirmBottom,
} from "client/components"

import FormatScientific from "client/pages/sil/format/scientific"
import FormatPercentage from "client/pages/sil/format/percentage"
import FormatProbability from "client/pages/sil/format/probability"

interface EditDeviceProps {
    device: SilDevice | SilProjectDevice
}

let isMounted: boolean

let updated = false
let recalculationRequired = false
let saveMode = 0

const Form = ({
    device,
}: EditDeviceProps) => {
    const baseRoute = useSelector((state: StoreType) => state.silDevice.baseDeviceRoute)
    const project = useSelector((state: StoreType) => state.project.projectView)
    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const packageUsage = useSelector((state: StoreType) => state.system.packageUsage)

    const modals = useSelector((state: StoreType) => state.system.modals)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [name, setName] = useInput(device.name)
    const [manufacturer, setManufacturer] = useInput(device.manufacturer)
    const [description, setDescription] = useInput(device.description)
    const [reference, setReference] = useInput(device.reference)

    const [logoId, setLogoId] = useState(device.logoId)
    const [logo, setLogo] = useState(device.logo)
    const [type, setType] = useState(device.type)
    const [lowDemandMode, setLowDemandMode] = useState(device.lowDemandMode)
    const [highDemandMode, setHighDemandMode] = useState(device.highDemandMode)
    const [continuousMode, setContinuousMode] = useState(device.continuousMode)
    const [logicSolver, setLogicSolver] = useState(device.logicSolver)
    const [provenInUse, setProvenInUse] = useState(device.provenInUse)
    const [failureRate, setFailureRate] = useState(device.failureRate)
    const [status, setStatus] = useState(device.status)

    const [sff, setSFF] = useState(device.sff)
    const [dc, setDC] = useState(device.dc)
    const [pfd, setPFD] = useState(device.pfd)
    const [pfh, setPFH] = useState(device.pfh)
    const [sil, setSil] = useState(device.sil)

    const [sd, setSD] = useState(device.sd)
    const [su, setSU] = useState(device.su)
    const [dd, setDD] = useState(device.dd)
    const [du, setDU] = useState(device.du)

    const [validation, setValidation] = useState<SilDeviceValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)
    const [recalculateCount, setRecalculateCount] = useState(0)

    const accountDevice = device as SilDevice
    const projectDevice = device as SilProjectDevice
    const isQidDevice = project.id && projectDevice.qidProductId !== null

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        if (!isMounted) return

        if (modals.length) {
            if (isMounted) dispatch(closeModal())
            return
        }

        const path = device.id
            ? `${baseRoute}/${VIEW}/${device.id}`
            : baseRoute

        forceRerenderContent(path, dispatch)
    }

    const onCancelConfirm = () => {
        setSaveState(IDLE)
        setApplyState(IDLE)
        setRecalculateCount(0)
    }

    const onSave = () => {
        if (saveMode === 1) setSaveState(PENDING)
        if (saveMode === 2) setApplyState(PENDING)

        LoadSilDeviceById(device.id, userData.accountId, project.id).then((existingDevice) => {
            updated = false
            recalculationRequired = false

            if (
                existingDevice && ( // saving on account/project level
                    existingDevice.manufacturer !== manufacturer
                    || existingDevice.name !== name
                    || existingDevice.description !== description
                    || existingDevice.reference !== reference
                    || existingDevice.logoId !== logoId
                    || existingDevice.logicSolver !== logicSolver
                    || existingDevice.failureRate !== failureRate
                    || existingDevice.provenInUse !== provenInUse
                    || existingDevice.type !== type
                    || existingDevice.lowDemandMode !== lowDemandMode
                    || existingDevice.highDemandMode !== highDemandMode
                    || existingDevice.continuousMode !== continuousMode
                    || existingDevice.sd !== sd
                    || existingDevice.su !== su
                    || existingDevice.dd !== dd
                    || existingDevice.du !== du
                    || existingDevice.sff !== sff
                    || existingDevice.dc !== dc
                    || existingDevice.pfd !== pfd
                    || existingDevice.pfh !== pfh
                    || existingDevice.sil !== sil
                    || existingDevice.status !== status
                )
            ) {
                updated = true
            }

            if (
                existingDevice && project.id && ( // saving on project level
                    existingDevice.type !== type
                    || existingDevice.lowDemandMode !== lowDemandMode
                    || existingDevice.highDemandMode !== highDemandMode
                    || existingDevice.continuousMode !== continuousMode
                    || existingDevice.logicSolver !== logicSolver
                    || existingDevice.provenInUse !== provenInUse
                    || existingDevice.failureRate !== failureRate
                    || existingDevice.sd !== sd
                    || existingDevice.su !== su
                    || existingDevice.dd !== dd
                    || existingDevice.du !== du
                    || existingDevice.sff !== sff
                    || existingDevice.dc !== dc
                    || existingDevice.pfd !== pfd
                    || existingDevice.pfh !== pfh
                    || existingDevice.sil !== sil
                )
            ) {
                recalculationRequired = true
            }

            if (recalculationRequired) {
                // check, if there are functions that use this device
                CountCalculatedSafetyFunctions({
                    projectId: project.id,
                    deviceId: projectDevice.deviceId,
                    projectDeviceId: !projectDevice.deviceId && !projectDevice.qidProductId ? projectDevice.id : null,
                    qidProductId: projectDevice.qidProductId,
                    qidModelId: projectDevice.qidModelId,
                    qidSafetyFunctionId: projectDevice.qidSafetyFunctionId,
                    qidOptionId: projectDevice.qidOptionId,
                    qidDatapointId: projectDevice.qidDatapointId,
                    qidMethod: projectDevice.qidMethod,
                }).then((count) => {
                    if (count) {
                        LoadAccountPackageUsage(userData.accountId)
                        .then((res) => {
                            dispatch(setSystemPackageUsage(res.packageUsage))
                            // show confirm
                            setRecalculateCount(count)
                        })
                    } else {
                        save()
                    }
                })
            } else {
                save()
            }
        })
    }

    const save = () => {
        const newDeviceData: SilDeviceCreate = {
            accountId: accountDevice.accountId || userData.accountId,
            manufacturer,
            name,
            logoId,
            description,
            reference,
            nested: false,
            type,
            lowDemandMode,
            highDemandMode,
            continuousMode,
            logicSolver,
            provenInUse,

            failureRate,
            sff,
            dc,
            pfd,
            pfh,
            sil,

            sd,
            su,
            dd,
            du,

            status,
            copy: device.copy || 0,
            updated: device.updated || false, // "updated" marker is not cleared
        }

        const newProjectDeviceData: SilProjectDeviceCreate = {
            ...newDeviceData,
            projectId: projectDevice.projectId || project.id,
            deviceId: projectDevice.deviceId || null,

            qidProductId: projectDevice.qidProductId || null,
            qidModelId: projectDevice.qidModelId || null,
            qidSafetyFunctionId: projectDevice.qidSafetyFunctionId || null,
            qidOptionId: projectDevice.qidOptionId || null,
            qidDatapointId: projectDevice.qidDatapointId || null,
            qidMethod: projectDevice.qidMethod || null,

            qidModelName: projectDevice.qidModelName || null,
            qidSafetyFunctionName: projectDevice.qidSafetyFunctionName || null,
            qidOptionName: projectDevice.qidOptionName || null,
            qidDatapointDate: projectDevice.qidDatapointDate || null,
        }

        const { maxDevices } = GetMaxSilDevices(packageSettings, userData)

        SaveSilDevice(
            device.id,
            project.id ? newProjectDeviceData : newDeviceData,
            project.id
                ? null // unlimited number of devices on project level
                : maxDevices,
            lang,
        ).then((res) => {
            const promiseArray: Promise<any>[] = []

            // update depending devices
            if (!res.validation) {
                // saving account device
                if (!project.id && updated) {
                    promiseArray.push(
                        // update project devices imported from account
                        SilProjectDeviceUpdateByFilters({ deviceId: device.id }),

                        // update account and project subsystems that based on this device
                        SilDeviceUpdateRequired({ deviceId: device.id }),
                        SilProjectDeviceUpdateRequired({ deviceId: device.id }),
                    )
                }

                // saving device on project level
                if (project.id && updated) {
                    // update project subsystems that based on this device
                    if (projectDevice.deviceId) { // device imported from account
                        promiseArray.push(
                            SilProjectDeviceUpdateRequired({ deviceId: projectDevice.deviceId }),
                        )
                    }
                    if (projectDevice.qidProductId) { // device imported from QID
                        promiseArray.push(
                            SilProjectDeviceUpdateRequired({
                                qidProductId: projectDevice.qidProductId,
                                qidModelId: projectDevice.qidModelId,
                                qidSafetyFunctionId: projectDevice.qidSafetyFunctionId,
                                qidOptionId: projectDevice.qidOptionId,
                                qidDatapointId: projectDevice.qidDatapointId,
                                qidMethod: projectDevice.qidMethod,
                            }),
                        )
                    }
                    if (!projectDevice.deviceId && !projectDevice.qidProductId) { // device created on project level
                        promiseArray.push(
                            SilProjectDeviceUpdateRequired({ projectDeviceId: projectDevice.id }),
                        )
                    }
                }

                // saving project device
                if (project.id) {
                    promiseArray.push(
                        UpdateSilResultProjectDevice({
                            projectId: project.id,
                            deviceId: projectDevice.deviceId,
                            projectDeviceId: !projectDevice.deviceId && !projectDevice.qidProductId ? projectDevice.id : null,
                            qidProductId: projectDevice.qidProductId,
                            qidModelId: projectDevice.qidModelId,
                            qidSafetyFunctionId: projectDevice.qidSafetyFunctionId,
                            qidOptionId: projectDevice.qidOptionId,
                            qidDatapointId: projectDevice.qidDatapointId,
                            qidMethod: projectDevice.qidMethod,
                        }),
                    )

                    if (recalculationRequired) {
                        promiseArray.push(
                            IncreaseSilCalculations(userData.accountId, dispatch),
                        )
                    }
                }
            }

            Promise.all(promiseArray).then(() => {
                if (isMounted) {
                    setValidation(res.validation)
                    setSaveState(IDLE)
                    setApplyState(IDLE)

                    if (res.newDeviceId) replaceHistoryState(`${baseRoute}/${EDIT}/${res.newDeviceId}`)
                }
                if (res.validation) return

                const updatedDevice: SilDevice = {
                    id: res.newDeviceId || device.id,
                    logo,
                    ...newDeviceData,
                }
                const updatedProjectDevice: SilProjectDevice = {
                    id: res.newDeviceId || device.id,
                    logo,
                    ...newProjectDeviceData,
                }
                dispatch(setSilDeviceEdit(project.id ? updatedProjectDevice : updatedDevice))

                dispatch(setSilDeviceReloadList(false))
                dispatch(setSilDeviceReloadView(false))
                setTimeout(() => {
                    dispatch(setSilDeviceReloadList(true))
                    dispatch(setSilDeviceReloadView(true))
                }, 0)

                if (saveMode === 1) onClose()
                if (saveMode === 2) {
                    dispatch(setSilDeviceReloadEdit(false))
                    setTimeout(() => {
                        dispatch(setSilDeviceReloadEdit(true))
                    }, 0)
                }
            })
        })
    }


    const { maxCalculations, usedCalculations } = packageUsage

    let leftCalculations = ""
    if (maxCalculations === null) leftCalculations = lang.unlimitedCalculations
    else leftCalculations = lang.leftCalculations.replace("{0}", usedCalculations.toString()).replace("{1}", maxCalculations.toString())

    return (
        <div>
            <PageForm>
                <Row>
                    <Col xs={12}>
                        <FormGroup>
                            <Label>{lang.name}</Label>
                            <Input type="text" value={name} onChange={setName} />
                            <Feedback>{validation && validation.name}</Feedback>
                        </FormGroup>
                        <FormGroup>
                            <Label>{lang.manufacturer}</Label>
                            <Input type="text" value={manufacturer} onChange={setManufacturer} />
                            <Feedback>{validation && validation.manufacturer}</Feedback>
                        </FormGroup>
                        <FormGroup>
                            <Label>{lang.description}</Label>
                            <Textarea value={description} onChange={setDescription} />
                        </FormGroup>
                        <FormGroup>
                            <Label>{lang.image}</Label>
                            <FileLoader
                                value={logo}
                                callback={(file) => {
                                    setLogoId(file.id)
                                    setLogo(file)
                                }}
                            />
                        </FormGroup>

                        {
                            !isQidDevice && (
                                <FormGroup>
                                    <Label>{lang.reference}</Label>
                                    <Input type="text" value={reference} onChange={setReference} />
                                    <Feedback>{validation && validation.reference}</Feedback>
                                </FormGroup>
                            )
                        }

                        <FormGroup>
                            <Label>{lang.status}</Label>
                            <FormGroup inline>
                                <RadioButton
                                    checked={status === PUBLISHED}
                                    onChange={() => setStatus(PUBLISHED)}
                                    title={lang.published}
                                />
                                <RadioButton
                                    checked={status === UNPUBLISHED}
                                    onChange={() => setStatus(UNPUBLISHED)}
                                    title={lang.unpublished}
                                />
                            </FormGroup>
                        </FormGroup>
                    </Col>
                </Row>
            </PageForm>


            <Collapse show title={lang.safetyDetails}>
                <PageForm>
                    <Row>
                        <Col xs={12}>
                            <FormGroup>
                                <Label>{lang.mode}</Label>
                                <CheckboxWrapper>
                                    <CheckButton
                                        checked={lowDemandMode}
                                        onChange={() => setLowDemandMode(!lowDemandMode)}
                                        title={lang.modeLowDemand}
                                    />
                                    <CheckButton
                                        checked={highDemandMode}
                                        onChange={() => setHighDemandMode(!highDemandMode)}
                                        title={lang.modeHighDemand}
                                    />
                                    <CheckButton
                                        checked={continuousMode}
                                        onChange={() => setContinuousMode(!continuousMode)}
                                        title={lang.modeContinuous}
                                    />
                                </CheckboxWrapper>
                            </FormGroup>
                            <FormGroup>
                                <Label>{lang.type}</Label>
                                <FormGroup inline>
                                    <RadioButton
                                        checked={type === TYPE_A}
                                        onChange={() => setType(TYPE_A)}
                                        title={TYPE_A}
                                    />
                                    <RadioButton
                                        checked={type === TYPE_B}
                                        onChange={() => setType(TYPE_B)}
                                        title={TYPE_B}
                                    />
                                </FormGroup>
                            </FormGroup>
                            <FormGroup>
                                <Label>{lang.logicSolver}</Label>
                                <FormGroup inline>
                                    <RadioButton
                                        checked={logicSolver}
                                        onChange={() => setLogicSolver(true)}
                                        title={lang.yes}
                                    />
                                    <RadioButton
                                        checked={!logicSolver}
                                        onChange={() => setLogicSolver(false)}
                                        title={lang.no}
                                    />
                                </FormGroup>
                            </FormGroup>
                            <FormGroup>
                                <Label>{lang.provenReliabilityData}</Label>
                                <FormGroup inline>
                                    <RadioButton
                                        checked={provenInUse === PROVEN_NO}
                                        onChange={() => setProvenInUse(PROVEN_NO)}
                                        title={lang.no}
                                    />
                                    <RadioButton
                                        checked={provenInUse === PROVEN_IN_USE}
                                        onChange={() => setProvenInUse(PROVEN_IN_USE)}
                                        title={lang.provenInUse}
                                    />
                                    <RadioButton
                                        checked={provenInUse === PRIOR_USE}
                                        onChange={() => setProvenInUse(PRIOR_USE)}
                                        title={lang.priorUse}
                                    />
                                </FormGroup>
                            </FormGroup>
                            <FormGroup>
                                <Label>{lang.use}</Label>
                                <FormGroup inline>
                                    <RadioButton
                                        checked={failureRate}
                                        onChange={() => setFailureRate(true)}
                                        title={lang.givenFailureRates}
                                    />
                                    <RadioButton
                                        checked={!failureRate}
                                        onChange={() => setFailureRate(false)}
                                        title={lang.givenSFF}
                                    />
                                </FormGroup>
                            </FormGroup>
                        </Col>
                    </Row>
                </PageForm>
            </Collapse>

            {
                failureRate && (
                    <Collapse show title={lang.fillInGivenFailureRates}>
                        <PageForm>
                            <Row>
                                <Col xs={12}>
                                    <FormatScientific
                                        title={lang.sd}
                                        data={sd}
                                        onChange={setSD}
                                        validation={
                                            (validation && validation.sd)
                                            || ((!sd || !sd.length) && lang.validationFieldRequired)
                                        }
                                    />
                                    <FormatScientific
                                        title={lang.su}
                                        data={su}
                                        onChange={setSU}
                                        validation={
                                            (validation && validation.su)
                                            || ((!su || !su.length) && lang.validationFieldRequired)
                                        }
                                    />
                                    <FormatScientific
                                        title={lang.dd}
                                        data={dd}
                                        onChange={setDD}
                                        validation={
                                            (validation && validation.dd)
                                            || ((!dd || !dd.length) && lang.validationFieldRequired)
                                        }
                                    />
                                    <FormatScientific
                                        title={lang.du}
                                        data={du}
                                        onChange={setDU}
                                        validation={
                                            (validation && validation.du)
                                            || ((!du || !du.length) && lang.validationFieldRequired)
                                        }
                                    />
                                </Col>
                            </Row>
                        </PageForm>
                    </Collapse>
                )
            }

            {
                !failureRate && (
                    <Collapse show title={lang.fillInGivenSFF}>
                        <PageForm>
                            <Row>
                                <Col xs={12}>
                                    <FormatPercentage
                                        title={lang.sff}
                                        data={sff}
                                        onChange={setSFF}
                                        validation={
                                            (validation && validation.sff)
                                            || ((!sff || !sff.length) && lang.validationFieldRequired)
                                        }
                                    />
                                    <FormatPercentage
                                        title={lang.dc}
                                        data={dc}
                                        onChange={setDC}
                                        validation={
                                            (validation && validation.dc)
                                            || ((!dc || !dc.length) && lang.validationFieldRequired)
                                        }
                                    />
                                    <FormatProbability
                                        title={lang.pfd}
                                        data={pfd}
                                        onChange={setPFD}
                                        validation={
                                            (validation && validation.pfd)
                                            || ((!pfd || !pfd.length) && lang.validationFieldRequired)
                                        }
                                    />
                                    <FormatProbability
                                        title={lang.pfh}
                                        data={pfh}
                                        onChange={setPFH}
                                        validation={
                                            (validation && validation.pfh)
                                            || ((!pfh || !pfh.length) && lang.validationFieldRequired)
                                        }
                                    />
                                    <FormGroup>
                                        <Label>{lang.sil}</Label>
                                        <SelectDropdown title={!sil ? lang.notGiven : lang[`sil_${sil}`]}>
                                            <SelectDropdownItem onClick={() => setSil(null)}>{lang.notGiven}</SelectDropdownItem>
                                            <SelectDropdownItem onClick={() => setSil(1)}>{lang.sil_1}</SelectDropdownItem>
                                            <SelectDropdownItem onClick={() => setSil(2)}>{lang.sil_2}</SelectDropdownItem>
                                            <SelectDropdownItem onClick={() => setSil(3)}>{lang.sil_3}</SelectDropdownItem>
                                            <SelectDropdownItem onClick={() => setSil(4)}>{lang.sil_4}</SelectDropdownItem>
                                        </SelectDropdown>
                                    </FormGroup>
                                </Col>
                            </Row>
                        </PageForm>
                    </Collapse>
                )
            }

            <PageForm>
                <Row>
                    <Col xs={12}>
                        <Button
                            variant="success"
                            row
                            onClick={() => {
                                saveMode = 1
                                onSave()
                            }}
                        >
                            {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                        </Button>
                        <Button
                            variant="success"
                            row
                            onClick={() => {
                                saveMode = 2
                                onSave()
                            }}
                        >
                            {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                        </Button>
                        <Button row onClick={onClose}>{lang.cancel}</Button>
                    </Col>
                </Row>

                <ConfirmWrapper
                    show={recalculateCount > 0}
                    onClose={onCancelConfirm}
                >
                    <Confirm align="center">
                        <ConfirmTitle>{lang.recalculationTitle}</ConfirmTitle>
                        <ConfirmDescription align="start">
                            {lang.recalculationDescription.replace("{0}", recalculateCount.toString())}
                        </ConfirmDescription>
                        <ConfirmDescription align="start">{leftCalculations}</ConfirmDescription>

                        <ConfirmBottom>
                            <Button row onClick={onCancelConfirm}>{lang.cancel}</Button>
                            <Button
                                variant="success"
                                row
                                disabled={maxCalculations !== null && usedCalculations >= maxCalculations}
                                onClick={() => {
                                    save()
                                    setRecalculateCount(0)
                                }}
                            >
                                {lang.confirm}
                            </Button>
                        </ConfirmBottom>
                    </Confirm>
                </ConfirmWrapper>
            </PageForm>
        </div>
    )
}
export default Form
